import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Plus, Pencil, Trash2, X, Save, Car, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '@/api';
import VehiclePicker from '@/components/VehiclePicker';

const PAGE = 50;
const EMPTY = {
  make: '', model: '', year_start: 2026, year_end: 2026,
  side_w: '', side_h: '', side_sqft: '', back_w: '', back_h: '', back_sqft: '',
  hood_w: '', hood_l: '', hood_sqft: '', roof_w: '', roof_l: '', roof_sqft: '', total_sqft: '', note: '',
};
const NUM_KEYS = ['side_w', 'side_h', 'side_sqft', 'back_w', 'back_h', 'back_sqft', 'hood_w', 'hood_l', 'hood_sqft', 'roof_w', 'roof_l', 'roof_sqft', 'total_sqft'];

const toPayload = (f) => {
  const p = { make: f.make.trim(), model: f.model.trim(), year_start: +f.year_start, year_end: +f.year_end, note: f.note || null };
  NUM_KEYS.forEach((k) => { p[k] = f[k] === '' || f[k] == null ? null : parseFloat(f[k]); });
  return p;
};
const fromDoc = (d) => {
  const f = { ...EMPTY, ...d, note: d.note || '' };
  NUM_KEYS.forEach((k) => { f[k] = d[k] == null ? '' : d[k]; });
  return f;
};

export default function VehicleDbTab() {
  const [q, setQ] = useState('');
  const [make, setMake] = useState('');
  const [makes, setMakes] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | doc
  const [showLookup, setShowLookup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: st }] = await Promise.all([
        api.get('/vehicles/search', { params: { q, make: make || undefined, limit: PAGE, skip } }),
        api.get('/vehicles/stats'),
      ]);
      setItems(s.items || []); setTotal(s.total || 0); setStats(st);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load vehicles');
    } finally { setLoading(false); }
  }, [q, make, skip]);

  useEffect(() => { api.get('/vehicles/makes').then(({ data }) => setMakes(data.makes || [])).catch(() => {}); }, [stats?.makes]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setSkip(0); }, [q, make]);

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.year} ${v.make} ${v.model}?`)) return;
    try { await api.delete(`/admin/vehicles/${v.id}`); toast.success('Vehicle deleted.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-vehicle-db">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mi-eyebrow text-[#E63946]">// VEHICLE DB · PVO PANEL SQFT</div>
          <div className="text-white/60 text-xs mt-1">
            {stats ? <>{stats.total.toLocaleString()} vehicles · {stats.makes} makes · {stats.estimated} estimated 2024-26 · {stats.custom} custom</> : 'Loading…'}
            {' '}· all measurements include 6" bleed.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowLookup((v) => !v)} data-testid="admin-vehicle-lookup-toggle" className="mi-btn-ghost text-xs">
            <Car className="w-3.5 h-3.5" /> {showLookup ? 'Hide lookup' : 'Quick lookup'}
          </button>
          <button onClick={() => setEditing('new')} data-testid="admin-vehicle-add-btn" className="mi-btn-red text-xs">
            <Plus className="w-3.5 h-3.5" /> Add vehicle
          </button>
        </div>
      </div>

      {showLookup && (
        <div className="border-2 border-[#E63946]/40 bg-[#E63946]/[0.04] p-5">
          <VehiclePicker testIdPrefix="admin-vp" />
        </div>
      )}

      {editing && (
        <VehicleForm
          initial={editing === 'new' ? EMPTY : fromDoc(editing)}
          isNew={editing === 'new'}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          id={editing === 'new' ? null : editing.id}
        />
      )}

      <div className="grid md:grid-cols-[1fr_220px] gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search make, model or year… e.g. Sprinter 170"
                 data-testid="admin-vehicle-search" className="mi-input pl-10" />
        </div>
        <select value={make} onChange={(e) => setMake(e.target.value)} data-testid="admin-vehicle-make-filter" className="mi-input">
          <option value="">All makes</option>
          {makes.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="border mi-line-strong overflow-x-auto">
        <table className="w-full text-xs" data-testid="admin-vehicle-table">
          <thead>
            <tr className="bg-black/60">
              {['VEHICLE', 'YEARS', 'SIDE', 'BACK', 'HOOD', 'ROOF', 'TOTAL', 'NO ROOF', 'SRC', ''].map((h) => (
                <th key={h} className="p-3 text-left mi-eyebrow text-white/50 text-[9px] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !items.length ? (
              <tr><td colSpan={10} className="p-6 text-white/50 text-center">Loading…</td></tr>
            ) : !items.length ? (
              <tr><td colSpan={10} className="p-6 text-white/50 text-center">No vehicles match.</td></tr>
            ) : items.map((v) => (
              <tr key={v.id} className="border-t mi-line hover:bg-white/[0.02]" data-testid={`vehicle-row-${v.id}`}>
                <td className="p-3 text-white whitespace-nowrap"><span className="text-white/60">{v.make}</span> {v.model}</td>
                <td className="p-3 font-mono text-white/70">{v.year}</td>
                <Num v={v.side_sqft} /><Num v={v.back_sqft} /><Num v={v.hood_sqft} /><Num v={v.roof_sqft} />
                <td className="p-3 font-mono text-[#E63946] font-bold">{v.total_sqft}</td>
                <td className="p-3 font-mono text-white/70">{v.has_roof ? Math.round((v.total_sqft - (v.roof_sqft || 0)) * 10) / 10 : v.total_sqft}</td>
                <td className="p-3">
                  <span className={`mi-tag text-[8px] ${v.source === 'admin' ? 'mi-tag-red' : v.estimated ? 'border-yellow-400/50 text-yellow-400' : ''}`}>
                    {v.source === 'admin' ? 'CUSTOM' : v.estimated ? 'EST' : v.edited_by_admin ? 'EDITED' : 'PVO'}
                  </span>
                </td>
                <td className="p-3 whitespace-nowrap">
                  <button onClick={() => setEditing(v)} data-testid={`vehicle-edit-${v.id}`} className="text-white/50 hover:text-white p-1" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(v)} data-testid={`vehicle-delete-${v.id}`} className="text-white/50 hover:text-[#E63946] p-1" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-white/50">
        <span data-testid="admin-vehicle-count">{total ? `${skip + 1}–${Math.min(skip + PAGE, total)} of ${total.toLocaleString()}` : '0 results'}</span>
        <div className="flex gap-2">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} className="mi-btn-ghost text-[10px] px-2 py-1 disabled:opacity-40"><ChevronLeft className="w-3 h-3" /> Prev</button>
          <button disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)} className="mi-btn-ghost text-[10px] px-2 py-1 disabled:opacity-40">Next <ChevronRight className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}

function Num({ v }) {
  return <td className="p-3 font-mono text-white/70">{v != null && v > 0 ? v : <span className="text-white/25">—</span>}</td>;
}

const PANEL_GROUPS = [
  { label: 'Side (each)', w: 'side_w', h: 'side_h', s: 'side_sqft' },
  { label: 'Back', w: 'back_w', h: 'back_h', s: 'back_sqft' },
  { label: 'Hood', w: 'hood_w', h: 'hood_l', s: 'hood_sqft' },
  { label: 'Roof', w: 'roof_w', h: 'roof_l', s: 'roof_sqft' },
];

function VehicleForm({ initial, isNew, onCancel, onSaved, id }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(initial), [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Auto-fill panel sqft from W×H (inches) when the sqft field is blank.
  const autoSqft = (g) => {
    const w = parseFloat(f[g.w]), h = parseFloat(f[g.h]);
    if (w > 0 && h > 0 && f[g.s] === '') setF((cur) => ({ ...cur, [g.s]: Math.round((w * h) / 144 * 10) / 10 }));
  };
  const computedTotal = () => {
    const n = (k) => parseFloat(f[k]) || 0;
    return Math.round((n('side_sqft') * 2 + n('back_sqft') + n('hood_sqft') + n('roof_sqft')) * 10) / 10;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(f);
      if (isNew) await api.post('/admin/vehicles', payload);
      else await api.put(`/admin/vehicles/${id}`, payload);
      toast.success(isNew ? 'Vehicle added.' : 'Vehicle updated.');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border-2 border-[#E63946]/40 bg-[#E63946]/[0.04] p-5 space-y-4" data-testid="admin-vehicle-form">
      <div className="flex items-center justify-between">
        <div className="mi-eyebrow text-[#E63946] text-[10px]">// {isNew ? 'ADD VEHICLE' : 'EDIT VEHICLE'}</div>
        <button type="button" onClick={onCancel} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <L label="Make"><input required value={f.make} onChange={set('make')} data-testid="vf-make" className="mi-input" /></L>
        <L label="Model"><input required value={f.model} onChange={set('model')} data-testid="vf-model" className="mi-input" /></L>
        <L label="Year from"><input required type="number" min={1950} max={2100} value={f.year_start} onChange={set('year_start')} data-testid="vf-year-start" className="mi-input" /></L>
        <L label="Year to"><input required type="number" min={1950} max={2100} value={f.year_end} onChange={set('year_end')} data-testid="vf-year-end" className="mi-input" /></L>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        {PANEL_GROUPS.map((g) => (
          <div key={g.label} className="border mi-line p-3 bg-black/40">
            <div className="mi-eyebrow text-[9px] text-white/60 mb-2">{g.label}</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input type="number" step="0.1" placeholder='W "' value={f[g.w]} onChange={set(g.w)} onBlur={() => autoSqft(g)} className="mi-input text-xs" />
              <input type="number" step="0.1" placeholder='H / L "' value={f[g.h]} onChange={set(g.h)} onBlur={() => autoSqft(g)} className="mi-input text-xs" />
            </div>
            <input type="number" step="0.1" placeholder="sqft" value={f[g.s]} onChange={set(g.s)} data-testid={`vf-${g.s}`} className="mi-input text-xs" />
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-[200px_1fr] gap-3 items-end">
        <L label={`Total sqft (blank = auto ${computedTotal()})`}>
          <input type="number" step="0.1" value={f.total_sqft} onChange={set('total_sqft')} data-testid="vf-total" className="mi-input" placeholder={String(computedTotal())} />
        </L>
        <L label="Note"><input value={f.note} onChange={set('note')} className="mi-input" placeholder="Optional — e.g. measured on-site 2026" /></L>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="vf-save" className="mi-btn-red text-xs"><Save className="w-3.5 h-3.5" /> {busy ? 'Saving…' : 'Save vehicle'}</button>
        <button type="button" onClick={onCancel} className="mi-btn-ghost text-xs">Cancel</button>
      </div>
    </form>
  );
}

function L({ label, children }) {
  return <label className="block"><div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">{label}</div>{children}</label>;
}
